import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Activity, Building2, CalendarDays, Stethoscope, Sparkles, Home, AlertTriangle, RotateCw } from 'lucide-react';
import CommonLoader from '../CommonLoader';
import ReadingsTrend from './ReadingsTrend';
import { ReadingItems } from './ReadingChips';
import { fetchHealthReadings } from '../../redux/healthRecordSlice';
import { formatDay } from '../health/healthFormat';
import { hhmm, recentPoints, recorderLine } from '../../utils/checkSets';
import { tidyName } from '../../utils/tidyName';

// THE "READINGS" SECTION OF THE HEALTH RECORD (check sets, Round A — Scene 6).
//
// Every live check reading of the person, from every hospital they are with,
// one group per visit (date, hospital, doctor), values printed from each
// reading's own snapshot. Number fields draw a small six-month trend.
//
// Who sees it is the server's answer (the same rule as the rest of the
// record); this panel only reads GET /health-record/:patientId/readings. In a
// visit (`appointmentId`) the server marks what is new since this doctor's
// previous visit, and `onlyNew` shows just that — the drawer's "New since
// last visit" filter.
//
// A reading taken by staff is shown as "Recorded by staff": the record names
// clinicians, not employees (the server sends no name for it) — recorderLine,
// shared with the visit's own block (utils/checkSets.js).

function ReadingsPanel({ patientId, appointmentId = null, onlyNew = false }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const state = useSelector((s) => s.healthRecord?.readings);
    const mine = state && state.patientId === patientId;

    useEffect(() => {
        if (!patientId) return;
        if (mine && (state.loading || (state.loaded && !state.error && state.appointmentId === appointmentId))) return;
        dispatch(fetchHealthReadings({ patientId, appointmentId }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patientId, appointmentId, dispatch]);

    const visits = useMemo(() => {
        const list = mine ? state.visits || [] : [];
        if (!onlyNew) return list;
        return list
            .map((v) => ({ ...v, readings: (v.readings || []).filter((r) => r.isNew === true) }))
            .filter((v) => v.readings.length > 0);
    }, [mine, state, onlyNew]);

    const trends = useMemo(
        () => (mine && !onlyNew ? (state.series || []).filter((s) => recentPoints(s).length >= 2) : []),
        [mine, state, onlyNew]
    );

    if (!mine || (state.loading && !state.loaded)) {
        return (
            <div data-testid="readings-panel-loading" className="flex flex-col items-center justify-center py-8">
                <CommonLoader />
                <span className="text-sm text-[#4ABDBD]">{t('checkSets.loading')}</span>
            </div>
        );
    }

    if (state.error) {
        return (
            <div role="alert" data-testid="readings-panel-error" className="mx-auto flex max-w-xl flex-col items-center gap-2 rounded-2xl border border-red-100 bg-red-50 p-6 text-center">
                <p className="flex items-center gap-1.5 text-sm text-red-700">
                    <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                    {t('checkSets.readings_record_failed')}
                </p>
                <button
                    type="button"
                    data-testid="readings-panel-retry"
                    onClick={() => dispatch(fetchHealthReadings({ patientId, appointmentId }))}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] px-4 py-2 text-sm font-semibold text-white shadow-lg"
                >
                    <RotateCw className="h-4 w-4" aria-hidden="true" />
                    {t('checkSets.retry')}
                </button>
            </div>
        );
    }

    if (visits.length === 0) {
        return (
            <p data-testid={onlyNew ? 'readings-panel-empty-new' : 'readings-panel-empty'} className="rounded-2xl border border-dashed border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
                {onlyNew ? t('checkSets.readings_new_empty') : t('checkSets.readings_empty')}
            </p>
        );
    }

    return (
        <div data-testid={onlyNew ? 'readings-panel-new' : 'readings-panel'} className="flex flex-col gap-4">
            {trends.length > 0 && (
                <section data-testid="readings-trends">
                    <h4 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-gray-400">
                        <Activity className="h-3.5 w-3.5" aria-hidden="true" />
                        {t('checkSets.trend_heading')}
                    </h4>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {trends.map((s) => <ReadingsTrend key={`${s.setId}-${s.key}`} series={s} />)}
                    </div>
                </section>
            )}

            <ul data-testid="readings-visits" className="flex flex-col gap-3">
                {visits.map((v, i) => (
                    <li
                        key={v.appointmentId || `home-${i}`}
                        data-testid={`readings-visit-${v.appointmentId || `home-${i}`}`}
                        className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm"
                    >
                        <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600">
                            <span className="inline-flex items-center gap-1 font-semibold text-gray-800">
                                <CalendarDays className="h-3.5 w-3.5 text-[#2D8A8A]" aria-hidden="true" />
                                {formatDay(v.date)}
                            </span>
                            {v.appointmentId ? (
                                <>
                                    {v.hospitalName && (
                                        <span className="inline-flex items-center gap-1">
                                            <Building2 className="h-3.5 w-3.5 text-[#2D8A8A]" aria-hidden="true" />
                                            {v.hospitalName}
                                        </span>
                                    )}
                                    {v.doctorName && (
                                        <span className="inline-flex items-center gap-1">
                                            <Stethoscope className="h-3.5 w-3.5 text-[#2D8A8A]" aria-hidden="true" />
                                            {tidyName(v.doctorName)}
                                            {v.doctorSpecialty ? <span className="text-gray-400">· {v.doctorSpecialty}</span> : null}
                                        </span>
                                    )}
                                </>
                            ) : (
                                <span className="inline-flex items-center gap-1">
                                    <Home className="h-3.5 w-3.5 text-[#2D8A8A]" aria-hidden="true" />
                                    {t('checkSets.home_reading')}
                                </span>
                            )}
                        </div>
                        <div className="flex flex-col gap-2">
                            {(v.readings || []).map((r) => (
                                <div key={r.readingId} data-testid={`readings-reading-${r.readingId}`} className="rounded-xl border border-gray-100 bg-gray-50/60 p-3">
                                    <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                                        <p className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                                            {r.setName}
                                            {r.isNew === true && (
                                                <span data-testid={`readings-new-${r.readingId}`} className="inline-flex items-center gap-1 rounded-full bg-[#D7A05C]/15 px-2 py-px text-[10px] font-bold text-[#9a6a2c]">
                                                    <Sparkles className="h-3 w-3" aria-hidden="true" />
                                                    {t('checkSets.new_badge')}
                                                </span>
                                            )}
                                        </p>
                                        <span className="text-[11px] text-gray-500">
                                            {recorderLine(r, t)} · {hhmm(r.recordedAt)}
                                        </span>
                                    </div>
                                    <ReadingItems items={r.items} dense testId={`readings-items-${r.readingId}`} />
                                </div>
                            ))}
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export default ReadingsPanel;
